// Organisatie → Privacyteam (the privacy-team roster: Org × User × Functie) — FRONTEND-BLUEPRINT §3.8;
// PLAN-W29. Lists members (user + functie), lets an admin add (user picker + functie picker) and remove.
// The functie picker is backed by the canonical Functie library, which is owner/counsel-seeded and MAY
// be empty — the add form then degrades to an honest note (you can still view the roster). Gated
// privacyteam_membership.read / .write / .delete; the user picker reuses tenant_user.read.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UserPlus, Trash2 } from "lucide-react";
import { ApiError } from "@sympllex/api-client";
import { api } from "../../lib/api";
import { Can } from "../../lib/rbac";
import { useI18n } from "../../lib/i18n";
import { PageHeader } from "../../components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataTable, type Column } from "@/components/data-table";

const errText = (e: unknown) => (e instanceof ApiError ? `Server ${e.status}: ${JSON.stringify(e.body)}` : (e as Error).message);

interface Member { id: string; userId: string; userName: string | null; userEmail: string; functieId: string; functieName: string; functieCode: string }
interface UserOpt { userId: string; email: string; fullName?: string | null }
interface FunctieOpt { id: string; code: string; name: string }

export function PrivacyteamRoute() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["privacyteam"] });
  const { data, isLoading, error } = useQuery({ queryKey: ["privacyteam"], queryFn: () => api.listPrivacyteam() as Promise<Member[]> });

  const columns: Column<Member>[] = [
    { key: "userName", header: t("pt.member"), render: (r) => <span className="font-medium">{r.userName || r.userEmail}</span> },
    { key: "userEmail", header: t("pt.email"), render: (r) => <span className="text-muted-foreground">{r.userEmail}</span> },
    { key: "functieName", header: t("pt.functie"), render: (r) => r.functieName },
    {
      key: "id", header: "", render: (r) => (
        <Can perm="privacyteam_membership.delete">
          <RemoveButton id={r.id} onDone={invalidate} />
        </Can>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t("pt.title")} description={t("pt.sub")} />
      <Can perm="privacyteam_membership.write"><AddMemberCard onAdded={invalidate} /></Can>
      {isLoading && <p className="text-sm text-muted-foreground">{t("common.loading")}</p>}
      {error && <p className="text-sm text-destructive">{errText(error)}</p>}
      {data && (data.length
        ? <DataTable rows={data as unknown as Record<string, unknown>[]} columns={columns as unknown as Column<Record<string, unknown>>[]} getRowId={(r) => String(r.id)} />
        : <p className="text-sm text-muted-foreground">{t("pt.empty")}</p>)}
    </div>
  );
}

function RemoveButton({ id, onDone }: { id: string; onDone: () => void }) {
  const { t } = useI18n();
  const mut = useMutation({ mutationFn: () => api.removePrivacyteamMember(id), onSuccess: onDone });
  return (
    <button type="button" className="inline-flex items-center gap-1 text-xs text-destructive hover:underline" onClick={() => mut.mutate()} disabled={mut.isPending}>
      <Trash2 className="size-3.5" aria-hidden />{t("crud.remove")}
    </button>
  );
}

function AddMemberCard({ onAdded }: { onAdded: () => void }) {
  const { t } = useI18n();
  const [userId, setUserId] = useState("");
  const [functieId, setFunctieId] = useState("");
  const users = useQuery({ queryKey: ["pt-users"], queryFn: () => api.listPrivacyteamUsers() as Promise<UserOpt[]> });
  const functies = useQuery({ queryKey: ["pt-functies"], queryFn: () => api.listPrivacyteamFuncties() as Promise<FunctieOpt[]> });
  const add = useMutation({
    mutationFn: () => api.addPrivacyteamMember({ userId, functieId }),
    onSuccess: () => { setUserId(""); setFunctieId(""); onAdded(); },
  });

  const noFuncties = functies.isSuccess && functies.data.length === 0;

  return (
    <Card className="max-w-2xl">
      <CardHeader><CardTitle className="flex items-center gap-2"><UserPlus className="size-4 text-primary" aria-hidden />{t("pt.add")}</CardTitle></CardHeader>
      <CardContent>
        {noFuncties ? (
          <p className="text-sm text-muted-foreground">{t("pt.noFuncties")}</p>
        ) : (
          <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); add.mutate(); }}>
            <div className="grid gap-1.5 min-w-56">
              <Label htmlFor="pt-user">{t("pt.member")}</Label>
              <Select value={userId} onValueChange={setUserId}>
                <SelectTrigger id="pt-user"><SelectValue placeholder={t("pt.pickUser")} /></SelectTrigger>
                <SelectContent>
                  {(users.data ?? []).map((u) => (<SelectItem key={u.userId} value={u.userId}>{u.fullName || u.email}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5 min-w-56">
              <Label htmlFor="pt-functie">{t("pt.functie")}</Label>
              <Select value={functieId} onValueChange={setFunctieId}>
                <SelectTrigger id="pt-functie"><SelectValue placeholder={t("pt.pickFunctie")} /></SelectTrigger>
                <SelectContent>
                  {(functies.data ?? []).map((f) => (<SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={add.isPending || !userId || !functieId}>{add.isPending ? t("crud.saving") : t("pt.add")}</Button>
            {add.isError && <p className="w-full text-sm text-destructive">{errText(add.error)}</p>}
          </form>
        )}
      </CardContent>
    </Card>
  );
}
